import {
  COOKIE_DOMAIN,
  COOKIE_PREFIX,
  OR_LOGOUT_URL,
  SHEPHERD_VET_AUTH_EXCHANGE_URL,
  TOKEN_VALIDATION_URL
} from '@/constants/env.js';

export { COOKIE_DOMAIN, COOKIE_PREFIX, OR_LOGOUT_URL, SHEPHERD_VET_AUTH_EXCHANGE_URL, TOKEN_VALIDATION_URL };

/** Existing session cookie; validated via SDK API (same check as MicroHitlWidget). */
export const OR_TOKEN_COOKIE = COOKIE_PREFIX;
/** Companion cookie from auth exchange; value is `ttl` from the API (ms). */
export const OR_EXPIRE_COOKIE = `${COOKIE_PREFIX}_expire`;

export function getCookie(name) {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1].trim()) : '';
}

/**
 * Session cookie holds URL-encoded JSON (stringified object); only `token` is sent to the validation API.
 */
export function parseTokenFromOrCookie(raw) {
  if (!raw) return {};
  try {
    const decoded = decodeURIComponent(raw.trim());
    const parsed = JSON.parse(decoded);
    return parsed || {};
  } catch (e) {
    console.error(`Failed to parse token from ${OR_TOKEN_COOKIE} cookie`, raw);
    return {};
  }
}

/**
 * Writes `COOKIE_PREFIX` from `payload` (string or object serialized to JSON) and `${COOKIE_PREFIX}_expire` from `ttl` (ms).
 * `Max-Age` is derived from `ttl` (seconds, floor(ttl/1000)) so both cookies expire with the session.
 */
export function setOrCookiesFromAuthResponse({ payload, ttl }) {
  const payloadStr = JSON.stringify(payload);
  if (!payloadStr) {
    throw new Error(
      'Invalid auth response: payload resolved to empty string (check payload shape)',
    );
  }
  const ttlMs = Number(ttl);
  const maxAgeSec = Math.max(0, Math.floor(ttlMs / 1000));
  const attrs = `; Max-Age=${maxAgeSec}; Path=/; SameSite=None; Secure; Domain=.${COOKIE_DOMAIN}`;

  const orValue = encodeURIComponent(payloadStr);
  const expireValue = encodeURIComponent(String(ttlMs));
  
  document.cookie = `${OR_TOKEN_COOKIE}=${orValue}${attrs}`;
  document.cookie = `${OR_EXPIRE_COOKIE}=${expireValue}${attrs}`;
}

/** Clears session cookies using the same cookie attributes as `setOrCookiesFromAuthResponse`. */
function clearOrCookies() {
  const attrs = `; Max-Age=0; Path=/; SameSite=None; Secure; Domain=.${COOKIE_DOMAIN}`;
  document.cookie = `${OR_TOKEN_COOKIE}=${attrs}`;
  document.cookie = `${OR_EXPIRE_COOKIE}=${attrs}`;
}

/**
 * Exchanges a Shepherd JWT for `{ payload, ttl }` and stores `COOKIE_PREFIX` + `${COOKIE_PREFIX}_expire` cookies.
 */
export async function exchangeJwtForOrCookie(jwt) {
  const token = (jwt || '').trim();
  if (!token) throw new Error('JWT is required');

  const res = await fetch(SHEPHERD_VET_AUTH_EXCHANGE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    const text = (await res.text().catch(() => '')).trim() || res.statusText;
    throw new Error(text || `Auth exchange failed (${res.status})`);
  }

  const data = await res.json();
  const { payload, ttl } = data || {}; 
  setOrCookiesFromAuthResponse({ payload, ttl });
  return data;
}

/**
 * If the session cookie is still accepted by the SDK API, the widget can skip JWT exchange.
 */
export async function isOrCookieTokenAllowed() {
  const { token } = parseTokenFromOrCookie(getCookie(OR_TOKEN_COOKIE));
  if (!token) return false;

  try {
    const res = await fetch(TOKEN_VALIDATION_URL, {
      method: 'GET',
      headers: { Authorization: token },
    });
    if (res.status !== 200) return false;
    const data = await res.json();
    return Boolean(data && data.allow === true);
  } catch {
    return false;
  }
}

/**
 * Revokes the token with OneReach (`DELETE` + `Authorization` from the session cookie), then removes session cookies.
 * Cookies are always cleared so the browser session ends even if the revoke request fails.
 */
export async function logoutOrCookieSession() {
  const { token } = parseTokenFromOrCookie(getCookie(OR_TOKEN_COOKIE));
  if (token) {
    try {
      await fetch(OR_LOGOUT_URL, {
        method: 'DELETE',
        headers: { Authorization: token },
      });
    } catch {
      // ignore network errors; still clear cookies below
    }
  }
  clearOrCookies();
}
